import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, Check, AlertCircle, Copy, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function CopyRow({ value }) {
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value);
        toast.success("Copied");
      }}
      className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1 font-mono text-xs hover:border-primary transition-colors"
    >
      <span className="truncate max-w-[260px]">{value}</span>
      <Copy className="h-3 w-3 shrink-0 text-muted-foreground" />
    </button>
  );
}

export default function CustomDomain() {
  const [state, setState] = useState(null);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tenant/domain");
    setState(data);
    if (data?.domain) setDomain(data.domain);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/domain", { domain });
      setState({ ...data, verified: false });
      toast.success("Domain added. Follow the DNS steps below, then verify.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save domain.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/domain/verify");
      setState((s) => ({ ...s, ...data }));
      toast.success("Domain verified!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this custom domain?")) return;
    await api.delete("/tenant/domain");
    setDomain("");
    setState({ domain: null, verified: false, cname_target: state?.cname_target });
    toast.success("Domain removed.");
  };

  const verified = state?.verified;
  const hasDomain = !!state?.domain;

  return (
    <div className="px-4 md:px-8 py-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Studio · White Label</div>
        <h1 className="font-display text-4xl">Custom domain</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-xl">
          Map your own domain (e.g. <span className="font-mono">app.mybrand.com</span>) so clients see
          your brand — not Kairosa — throughout the entire experience.
        </p>
      </div>

      {/* Status banner */}
      {hasDomain && (
        <div
          data-testid="domain-status"
          className={
            "mb-6 rounded-xl border p-4 flex items-center gap-3 " +
            (verified
              ? "border-accent/50 bg-accent/10"
              : "border-border bg-secondary/50")
          }
        >
          {verified ? (
            <ShieldCheck className="h-5 w-5 text-accent" />
          ) : (
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{state.domain}</div>
            <div className="text-xs text-muted-foreground">
              {verified
                ? "Verified · Your brand loads at this domain."
                : "Pending verification · Complete the DNS steps below."}
            </div>
          </div>
          {!verified && (
            <Button
              data-testid="verify-domain-btn"
              onClick={verify}
              disabled={busy}
              size="sm"
              className="rounded-full"
            >
              {busy ? "Verifying…" : "Verify now"}
            </Button>
          )}
          <button
            onClick={remove}
            className="text-muted-foreground hover:text-destructive p-2"
            aria-label="Remove domain"
            data-testid="remove-domain-btn"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Add/update form */}
      <form onSubmit={save} className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div>
          <Label className="flex items-center gap-2">
            <Globe className="h-4 w-4" /> Your custom domain
          </Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              data-testid="domain-input"
              placeholder="app.mybrand.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="flex-1 font-mono"
              required
            />
            <Button
              data-testid="save-domain-btn"
              type="submit"
              disabled={busy}
              className="rounded-full"
            >
              {hasDomain ? "Update" : "Add domain"}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            We recommend a subdomain like <span className="font-mono">app.</span> or <span className="font-mono">portal.</span>
          </div>
        </div>
      </form>

      {/* DNS instructions */}
      {hasDomain && state?.verification_token && (
        <div className="mt-6 rounded-xl border border-border bg-card p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
            DNS records to add
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Add these two records in your DNS provider (Cloudflare, Namecheap, GoDaddy, Route53, etc.).
            Propagation usually takes 1–30 minutes.
          </p>

          <div className="rounded-lg border border-border overflow-hidden">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border bg-secondary/40 px-4 py-2.5">
              <div className="col-span-2">Type</div>
              <div className="col-span-5">Host / Name</div>
              <div className="col-span-5">Value</div>
            </div>

            <div className="grid grid-cols-12 items-center px-4 py-3 border-b border-border text-sm">
              <div className="col-span-2 font-mono text-xs">CNAME</div>
              <div className="col-span-5"><CopyRow value={state.domain} /></div>
              <div className="col-span-5"><CopyRow value={state.cname_target} /></div>
            </div>

            <div className="grid grid-cols-12 items-center px-4 py-3 text-sm">
              <div className="col-span-2 font-mono text-xs">TXT</div>
              <div className="col-span-5"><CopyRow value={state.verification_host} /></div>
              <div className="col-span-5"><CopyRow value={state.verification_token} /></div>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <Button
              data-testid="verify-domain-btn-2"
              onClick={verify}
              disabled={busy || verified}
              className="rounded-full gap-2"
            >
              {verified ? <Check className="h-4 w-4" /> : null}
              {verified ? "Verified" : busy ? "Verifying…" : "I've added the records — verify"}
            </Button>
            <span className="text-xs text-muted-foreground">
              We look up the TXT record to prove ownership.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
